import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import Swal from "sweetalert2";

// MUI imports
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
} from "@mui/material";
import { CloudDownload, Build } from "@mui/icons-material";

export default function Images({ token }) {
  const [imageName, setImageName] = useState("");
  const [contextPath, setContextPath] = useState("");
  const [tag, setTag] = useState("");
  const [loading, setLoading] = useState(false);

  const showMessage = (title, text, icon = "success") => {
    Swal.fire({
      title,
      text,
      icon,
      confirmButtonColor: "#1976d2", // MUI primary
    });
  };

  const pullImage = async () => {
    if (!imageName) return showMessage("Error", "Please enter image name", "error");
    setLoading(true);
    try {
      await axios.post(
        "https://api.harishacrypto.xyz/service18/api/images/pull",
        { image: imageName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showMessage("Success", `Pulled image: ${imageName}`);
      setImageName("");
    } catch (e) {
      showMessage("Error", e.response?.data?.error || e.message, "error");
    }
    setLoading(false);
  };

  const buildImage = async () => {
    if (!contextPath || !tag)
      return showMessage("Error", "Please enter context path and tag", "error");
    setLoading(true);
    try {
      await axios.post(
        "https://api.harishacrypto.xyz/service18/api/images/build",
        { contextPath, tag },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showMessage("Success", `Built image: ${tag}`);
      setContextPath("");
      setTag("");
    } catch (e) {
      showMessage("Error", e.response?.data?.error || e.message, "error");
    }
    setLoading(false);
  };

  return (
    <Box sx={{ p: 4 }}>
      {/* Heading */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          🖼️ Manage Images
        </Typography>
      </motion.div>

      <Stack spacing={3}>
        {/* Pull Image Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Card sx={{ borderRadius: 3, boxShadow: 6 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pull Image
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Image name to pull"
                  value={imageName}
                  onChange={(e) => setImageName(e.target.value)}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={pullImage}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <CloudDownload />}
                >
                  Pull Image
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </motion.div>

        {/* Build Image Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Card sx={{ borderRadius: 3, boxShadow: 6 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Build Image
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems="center"
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Build context path"
                  value={contextPath}
                  onChange={(e) => setContextPath(e.target.value)}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Tag"
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                />
                <Button
                  variant="contained"
                  color="success"
                  onClick={buildImage}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <Build />}
                >
                  Build Image
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </motion.div>
      </Stack>
    </Box>
  );
}
